import React, { useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Card } from "rebass";
import { toastr } from "react-redux-toastr";
import { extension } from "mime-types";

import { FieldProps } from "formik";

import { MAX_FILE_SIZE } from "./formSettings";
import { convertEntry } from "./convertEntry";

import { Placeholder } from "./Placeholder";

import { nFormatter } from "../../../../utils/nFormatter";
import { FileList } from "./FileList";

interface IProps extends FieldProps {
  maxSize: number;
  accept: string[];
}

export const InputFile: React.FC<IProps> = (
  {
    field: { name, value },
    form: { setFieldValue },
    maxSize,
    accept,
  }
) => {
  /* convert value Files to Entries */
  const entries = useMemo(() => (value as File[]).map(convertEntry), [value]);

  const onDropAccepted = useCallback((files: Array<File>) => {
    setFieldValue(name, [...value, ...files]);
  }, [name, value, setFieldValue]);
  const onDropRejected = useCallback((files: Array<File>) => {
    files.forEach(({ name, size, type }) => {
      const ext = extension(type);
      const reason: string[] = [];

      if (size > MAX_FILE_SIZE) {
        reason.push(`${name} size more than ${nFormatter(maxSize, 1)}B.`);
      }

      if (!ext || !accept.includes(`.${ext}`)) {
        reason.push(`${name} type is not supported.`);
      }

      toastr.error("Import File", reason.join("."));
    });
  }, [accept, maxSize]);

  const {
    getRootProps,
    getInputProps,
    open,
  } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept,
    multiple: true,
    maxSize,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <Card
      height="100%"
      border="1px dashed"
      borderColor="sp-gray1"
      borderRadius="10px"
      bg="#fff"
      css="display: flex; flex-direction: column; outline: none; overflow: hidden;"
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      {
        entries.length
          ? <FileList entries={entries} openFileSelection={open} />
          : <Placeholder accept={accept} openFileSelection={open} />
      }
    </Card>
  );
};
